import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { nanoid } from 'nanoid';

import {
  closeDialogAction,
  openDialogAction,
  updateDialogOptionsAction,
} from 'store/global/actions';

import CicSaveReasonModal from '../..';
import { CIC_SAVE_REASON_MODAL_HEADER } from '../../CicSaveReasonModal.constants';

import { getNoteError } from './utils';

export const useOpenCicSaveReasonModal = () => {
  const dispatch = useDispatch();

  const modalId = useMemo(() => nanoid(), []);

  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const triggerFieldsRef = useRef([]);
  const resolveRef = useRef(null);

  const renderBody = useCallback(
    ({ currentNote, currentError, onNoteChange }) =>
      () => (
        <CicSaveReasonModal
          error={currentError}
          note={currentNote}
          triggerFields={triggerFieldsRef.current}
          onNoteChange={onNoteChange}
        />
      ),
    [],
  );

  const onNoteChange = useCallback(e => {
    const { value } = e.target;

    setNote(value);
    setError(getNoteError(value));
  }, []);

  const onCancel = useCallback(() => {
    resolveRef.current?.(null);
    resolveRef.current = null;
  }, []);

  const onAccept = useCallback(() => {
    const validationError = getNoteError(note);

    if (validationError) {
      setError(validationError);

      return;
    }

    resolveRef.current?.({ note: note.trim() });
    resolveRef.current = null;

    dispatch(closeDialogAction(modalId));
  }, [dispatch, modalId, note]);

  useEffect(() => {
    dispatch(
      updateDialogOptionsAction(modalId, {
        isAcceptButtonDisabled: !!getNoteError(note),
        onAcceptClick: onAccept,
        body: renderBody({ currentNote: note, currentError: error, onNoteChange }),
      }),
    );
  }, [dispatch, modalId, note, error, onAccept, onNoteChange, renderBody]);

  const openCicSaveReasonModal = useCallback(
    triggerFields => {
      triggerFieldsRef.current = triggerFields ?? [];

      setNote('');
      setError('');

      dispatch(
        openDialogAction({
          id: modalId,
          header: CIC_SAVE_REASON_MODAL_HEADER,
          isRestyled: true,
          size: ModalSize.SMALL,
          withButtons: true,
          withClosingConfirmation: true,
          onAcceptButtonName: 'Save',
          isAcceptButtonDisabled: true,
          onAcceptClick: onAccept,
          afterCloseCallback: onCancel,
          body: renderBody({ currentNote: '', currentError: '', onNoteChange }),
        }),
      );

      return new Promise(resolve => {
        resolveRef.current = resolve;
      });
    },
    [dispatch, modalId, onAccept, onCancel, onNoteChange, renderBody],
  );

  return openCicSaveReasonModal;
};
